import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, ArrowLeft, Copy, Check, Clock, 
  CheckCircle, XCircle, Loader2 
} from 'lucide-react';
import Button from '../components/ui/Button';
import ThemeToggle from '../components/ThemeToggle';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface GiftCode {
  id: string;
  code: string;
  plan: string;
  created_at: string;
  expires_at: string;
  redeemed_at: string | null;
  status: 'active' | 'expired' | 'claimed';
  redeemed_by: string | null;
}

const GiftsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [giftCodes, setGiftCodes] = useState<GiftCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const fetchGiftCodes = async () => {
      try {
        const { data: codes, error } = await supabase
          .from('gift_codes')
          .select('*')
          .eq('creator_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Get status for each code
        const codesWithStatus = await Promise.all(
          codes.map(async (code) => {
            const { data: status, error: statusError } = await supabase
              .rpc('get_gift_code_status', { code_param: code.code });

            if (statusError) throw statusError;

            return {
              ...code,
              ...status
            };
          })
        );

        setGiftCodes(codesWithStatus);
      } catch (error: any) {
        console.error('Error fetching gift codes:', error);
        toast.error('Failed to fetch gift codes');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGiftCodes();
  }, [user, navigate]);

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/redeem?code=${code}`
      );
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast.success('Gift link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500">
            <Clock className="w-3 h-3 mr-1" />
            Expired
          </span>
        );
      case 'claimed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
            <XCircle className="w-3 h-3 mr-1" />
            Claimed
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading gift codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <div className="fixed top-0 left-0 right-0 border-b bg-background/80 backdrop-blur-sm z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/settings')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Settings
          </Button>
          <ThemeToggle />
        </div>
      </div>

      <div className="container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">My Gift Codes</h1>
              <p className="text-muted-foreground">
                Manage and track your gift codes
              </p>
            </div>
          </div>

          {giftCodes.length === 0 ? (
            <div className="text-center py-12">
              <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Gift Codes</h3>
              <p className="text-muted-foreground mb-6">
                You haven't created any gift codes yet
              </p>
              <Button onClick={() => navigate('/pricing')}>
                Create a Gift
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {giftCodes.map((gift) => (
                <motion.div
                  key={gift.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative overflow-hidden rounded-lg border bg-card p-6"
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(gift.status)}
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted">
                          CodeNest {gift.plan.charAt(0).toUpperCase() + gift.plan.slice(1)}
                        </span>
                      </div>
                      <div className="font-mono text-sm mb-1">
                        {gift.code}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Created on {new Date(gift.created_at).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {gift.status === 'claimed' && gift.redeemed_by && (
                        <div className="text-right">
                          <div className="text-sm font-medium">
                            Claimed by {gift.redeemed_by}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            on {new Date(gift.redeemed_at!).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      
                      {gift.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(gift.code)}
                        >
                          {copiedCode === gift.code ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftsPage;